/**
 * Entry-point source for the give flow. Shared with APO-8738's
 * `Church.giveOnWebAllowedSources` so keys never drift.
 */
export const GIVE_SOURCES = ["app", "app_clip", "web_embed", "web"] as const;
export type GiveSource = (typeof GIVE_SOURCES)[number];
export const DEFAULT_GIVE_SOURCE: GiveSource = "web";

const GIVE_SOURCE_SET: ReadonlySet<string> = new Set(GIVE_SOURCES);

/**
 * Snake_case `source` (URL param + cluster config) -> camelCase GraphQL key on
 * `Church.giveOnWebAllowedSources`. Mirrors `GIVE_ON_WEB_SOURCE_KEY_MAP` on the
 * cluster (APO-8738) so the admin and resolver agree on the mapping in one
 * place.
 */
export type GiveSourceGraphQLKey = "app" | "appClip" | "webEmbed" | "web";
export const GIVE_SOURCE_GRAPHQL_KEY: Record<GiveSource, GiveSourceGraphQLKey> =
  Object.freeze({
    app: "app",
    app_clip: "appClip",
    web_embed: "webEmbed",
    web: "web",
  });

/** Per-source toggles from `Church.giveOnWebAllowedSources` (APO-8738). */
export interface GiveOnWebAllowedSources {
  app: boolean;
  appClip: boolean;
  webEmbed: boolean;
  web: boolean;
}

/**
 * Pure check for the per-source toggle (APO-8738). Missing object or missing
 * key resolves to `false` to match the cluster resolver's contract: when
 * `GIVING.ALLOWED_WEB_SOURCES` config is missing or malformed, every source
 * comes back `false`.
 *
 * Lives here (rather than in `givingRedirect.server`) so callers and tests can
 * use it without pulling in that module's server-only dependencies.
 */
export function isSourcePermittedByToggle(
  graphqlKey: GiveSourceGraphQLKey,
  giveOnWebAllowedSources: GiveOnWebAllowedSources | null | undefined
): boolean {
  return giveOnWebAllowedSources?.[graphqlKey] === true;
}

/** The web embed sets `embedMode` but no `source`, so we derive web_embed. */
export function readGiveSource(request: Request): GiveSource {
  const params = new URL(request.url).searchParams;
  const embedMode = params.get("embedMode");
  if (embedMode && embedMode !== "none") return "web_embed";
  const raw = params.get("source");
  return raw && GIVE_SOURCE_SET.has(raw)
    ? (raw as GiveSource)
    : DEFAULT_GIVE_SOURCE;
}

/** Appends (or replaces) `source` on `target` to match the incoming request. */
export function withGiveSource(target: string, request: Request): string {
  const source = readGiveSource(request);
  if (source === DEFAULT_GIVE_SOURCE) return target;

  const isRelative = target.startsWith("/") || !target.includes("://");
  const parsed = new URL(target, "http://__apollos_internal__");
  parsed.searchParams.set("source", source);
  if (!isRelative) return parsed.toString();
  const qs = parsed.searchParams.toString();
  return qs ? `${parsed.pathname}?${qs}` : parsed.pathname;
}
